import { IRotationNode } from '../types/ViewModel/IRotationNode';
import { BerthCall } from '../types/ViewModel/BerthCall';
import { CargoOperation } from '../types/ViewModel/CargoOperation';
import { PortCall } from '../types/ViewModel/PortCall';
import { Rotation } from '../types/ViewModel/Rotation';
import { LatestVoyageData } from '../types/ViewModel/LatestVoyageData';

export const getParentBerthCallFromNode = (rotationNode: IRotationNode): BerthCall | null => {
    const cargo: CargoOperation = rotationNode as CargoOperation;
    return cargo.berthCall;
};

export const getParentPortCallFromNode = (rotationNode: IRotationNode | null): PortCall | null => {
    const bc: BerthCall = rotationNode as BerthCall;
    return bc.portCall;
};

/**
 * Port-call count alone is not sufficient to detect that a rotation's cargo operation content
 * changed — a rotation with zero cargo operations and its valid replacement can have the exact
 * same port-call count. Consumers that derive UI state from a rotation (e.g. rebuilding the
 * event-reconciled tree) must include this signature as a dependency so a same-count replacement
 * with different cargo operations is not silently ignored.
 *
 * The signature nests the actual identity/association tuples — portCallId -> berthCallId (+
 * berthId, so a berth reassignment changes the signature even when the berthCallId itself is
 * stable) -> sorted cargoOperationIds — rather than an aggregate count/sum, so it cannot collide
 * for different port/berth/cargo tuples and it detects berth reassignment. Built from primitive
 * ids only (no object references), so it cannot introduce reference-identity dependency loops in
 * consumers that memoize on it.
 */
export const getRotationCargoSignature = (rotation: Rotation | null | undefined): string => {
    if (!rotation) {
        return '';
    }

    const portCallSignatures = rotation.portCallArray.map((pc) => {
        const berthCallSignatures = (pc.berthCallArray ?? []).map((bc) => {
            const cargoOperationIds = (bc.cargoOperationArray ?? [])
                .map((co) => (co.cargoOperationId != null ? String(co.cargoOperationId) : 'null'))
                .sort();
            return `${bc.berthCallId ?? 'null'}/${bc.berthId ?? 'null'}:[${cargoOperationIds.join(',')}]`;
        });
        return `${pc.portCallId ?? 'null'}:{${berthCallSignatures.join(';')}}`;
    });

    return portCallSignatures.join('|');
};

export const getAllUniqueVoyageNumbers = (rotation: Rotation, latestVoyageData: LatestVoyageData, includeLatest = false): string[] => {
    const voyList: string[] = [];
    rotation.portCallArray.forEach((pc) => {
        if (pc.voyageNumber1 && !voyList.find((o) => o === pc.voyageNumber1)) voyList.push(pc.voyageNumber1);
        if (pc.voyageNumber2 && !voyList.find((o) => o === pc.voyageNumber2)) voyList.push(pc.voyageNumber2);
    });
    if (includeLatest) {
        return voyList.sort().reverse();
    }
    return voyList
        .filter((o) => !latestVoyageData || +o !== latestVoyageData.voyageNumber1)
        .sort()
        .reverse();
};

// Inserts a port call into the rotation at the end of other port calls with the same voyage number
// If the portcall belongs to a voyage before (lower number) than all other port calls in currently loaded rotation, it will be inserted before them
export const insertPortCallBasedOnVoyageNumber = (rotation: Rotation, portCall: PortCall, selectedVoyages: string[]): void => {
    const voyageNumber = portCall.voyageNumber1 || portCall.voyageNumber2;

    if (!selectedVoyages?.includes(voyageNumber)) {
        return; // Do not insert if the voyage number is not in the selected voyages
    }

    const indexOfFirstPortCallWithHigherVoyageNumber = rotation.portCallArray.findIndex(
        (pc) => pc.voyageNumber1 && +pc.voyageNumber1 >= +voyageNumber
    );

    if (indexOfFirstPortCallWithHigherVoyageNumber !== -1) {
        // Insert before the first port call with a higher voyage number
        rotation.portCallArray.splice(indexOfFirstPortCallWithHigherVoyageNumber, 0, portCall);
    } else {
        rotation.portCallArray.push(portCall);
    }
};
